import React, { useEffect, useMemo, useState } from 'react';
import { fetchWithAuth } from '../../utils/authUtils';
import '../../css/adminDashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const roleOptions = [
  { label: 'Tất cả vai trò', value: '' },
  { label: 'Elderly', value: 'elderly' },
  { label: 'Relative', value: 'relative' },
  { label: 'Admin', value: 'admin' },
];

const statusOptions = [
  { label: 'Tất cả trạng thái', value: '' },
  { label: 'Hoạt động', value: 'active' },
  { label: 'Không hoạt động', value: 'inactive' },
];

// Helper function to format date to Vietnam time
const formatVietnamDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    return dateString;
  }
};

function Toolbar({ q, setQ, role, setRole, status, setStatus, onSearch, onCreate }) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Tìm theo tên, email, SĐT..."
        style={{ flex: '1 1 260px', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6 }}
      />
      <select value={role} onChange={(e) => setRole(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        {roleOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        {statusOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <button onClick={onSearch} className="tamcare-btn-primary">Tìm</button>
      <button onClick={onCreate} className="tamcare-btn-ghost">Tạo tài khoản</button>
    </div>
  );
}

function Pagination({ page, limit, total, onPage }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 12 }}>
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="tamcare-btn-ghost">«</button>
      <span>Trang {page}/{totalPages}</span>
      <button disabled={page >= totalPages} onClick={() => onPage(page + 1)} className="tamcare-btn-ghost">»</button>
    </div>
  );
}

export default function AdminUsers() {
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', data?: user }
  const [form, setForm] = useState({ username: '', email: '', phone: '', role: 'elderly', fullName: '', password: '', is_active: true });

  const headers = useMemo(() => ([
    { key: 'username', label: 'Username' },
    { key: 'fullName', label: 'Họ tên' },
    { key: 'role', label: 'Vai trò' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'SĐT' },
    { key: 'isActive', label: 'Trạng thái' },
    { key: 'created_at', label: 'Tạo lúc', valueKey: 'createdAt' },
    { key: 'last_login_at', label: 'Đăng nhập gần nhất', valueKey: 'lastLoginAt' },
  ]), []);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ page: String(page), limit: String(limit), sort, order });
      if (q) params.set('q', q);
      if (role) params.set('role', role);
      if (status) params.set('status', status);
      const url = `${API_BASE}/api/admin/users?${params.toString()}`;
      const res = await fetchWithAuth(url);
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.success) throw new Error(payload?.message || 'Không thể tải danh sách');
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(Number(payload.total || 0));
    } catch (err) {
      setError(err?.message || 'Lỗi tải dữ liệu');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sort, order]);

  const onSearch = () => {
    setPage(1);
    load();
  };

  const toggleSort = (k) => {
    const nextOrder = sort === k ? (order === 'asc' ? 'desc' : 'asc') : 'asc';
    setSort(k);
    setOrder(nextOrder);
  };

  const openCreate = () => {
    setForm({ username: '', email: '', phone: '', role: 'elderly', fullName: '', password: '', is_active: true });
    setModal({ mode: 'create' });
  };

  const openEdit = (u) => {
    setForm({ username: u.username || '', email: u.email || '', phone: u.phone || '', role: u.role || 'elderly', fullName: u.fullName || '', password: '', is_active: !!u.isActive });
    setModal({ mode: 'edit', data: u });
  };

  const submitCreate = async () => {
    try {
      if (!form.username || !form.password || !form.fullName) {
        alert('Vui lòng nhập đủ username, password, fullName');
        return;
      }
      const res = await fetchWithAuth(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.success) throw new Error(payload?.message || 'Tạo tài khoản thất bại');
      setModal(null);
      setPage(1);
      load();
    } catch (err) {
      alert(err?.message || 'Lỗi tạo tài khoản');
    }
  };

  const submitEdit = async () => {
    try {
      const id = modal?.data?.id;
      if (!id) return;
      const res = await fetchWithAuth(`${API_BASE}/api/admin/users/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username,
          email: form.email,
          phone: form.phone,
          role: form.role,
          fullName: form.fullName,
          is_active: form.is_active,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.success) throw new Error(payload?.message || 'Cập nhật thất bại');
      setModal(null);
      load();
    } catch (err) {
      alert(err?.message || 'Lỗi cập nhật');
    }
  };

  const deactivateUser = async (u) => {
    if (!window.confirm(`Vô hiệu hóa tài khoản ${u.username}?`)) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/users/${u.id}`, { method: 'DELETE' });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.success) throw new Error(payload?.message || 'Vô hiệu hóa thất bại');
      load();
    } catch (err) {
      alert(err?.message || 'Lỗi khi vô hiệu hóa');
    }
  };

  const activateUser = async (u) => {
    if (!window.confirm(`Kích hoạt lại tài khoản ${u.username}?`)) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/users/${u.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: true }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.success) throw new Error(payload?.message || 'Kích hoạt thất bại');
      load();
    } catch (err) {
      alert(err?.message || 'Lỗi khi kích hoạt');
    }
  };

  return (
    <div className="admin-page-container">
      <div style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0, color: 'var(--primary)' }}><i className="fa-solid fa-users"></i> Quản lý người dùng</h1>
        <p style={{ color: '#64748b', marginTop: 6 }}>Tìm kiếm, sắp xếp, tạo và chỉnh sửa người dùng hệ thống</p>
      </div>

      <Toolbar q={q} setQ={setQ} role={role} setRole={setRole} status={status} setStatus={setStatus} onSearch={onSearch} onCreate={openCreate} />

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              {headers.map((h) => (
                <th key={h.key} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => toggleSort(h.key)}>
                  {h.label} {sort === h.key ? (order === 'asc' ? '▲' : '▼') : ''}
                </th>
              ))}
              <th style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={headers.length + 1} style={{ padding: 16 }}><i className="fa-solid fa-spinner fa-spin"></i> Đang tải...</td></tr>
            ) : error ? (
              <tr><td colSpan={headers.length + 1} style={{ padding: 16, color: '#ef4444' }}>{error}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={headers.length + 1} style={{ padding: 16 }}>Không có dữ liệu</td></tr>
            ) : (
              rows.map((u) => {
                const roleColor = u.role === 'elderly' ? '#1e90ff' : u.role === 'relative' ? '#ffa502' : u.role === 'admin' ? '#2f3542' : '#a4b0be';
                const statusColor = u.isActive ? '#10b981' : '#ef4444';
                return (
                <tr key={u.id} style={{ borderLeft: `4px solid ${roleColor}` }}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{u.username}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{u.fullName || '-'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', textTransform: 'capitalize' }}>{u.role}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{u.email || '-'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{u.phone || '-'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    {u.isActive ? <span style={{ color: '#10b981' }}>Hoạt động</span> : <span style={{ color: '#ef4444' }}>Không hoạt động</span>}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{formatVietnamDate(u.createdAt)}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{formatVietnamDate(u.lastLoginAt)}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <button className="tamcare-btn-ghost" onClick={() => openEdit(u)} title="Chỉnh sửa"><i className="fa-solid fa-pen-to-square"></i></button>{' '}
                    {u.isActive ? (
                      <button className="tamcare-btn-ghost" onClick={() => deactivateUser(u)} title="Khóa tài khoản"><i className="fa-solid fa-user-slash"></i></button>
                    ) : (
                      <button className="tamcare-btn-ghost" onClick={() => activateUser(u)} title="Kích hoạt tài khoản"><i className="fa-solid fa-user-check"></i></button>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} limit={limit} total={total} onPage={setPage} />

      {modal && (
        <div className="tamcare-followup-overlay" role="dialog" aria-modal="true" onClick={() => setModal(null)}>
          <div className="tamcare-followup-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="tamcare-followup-header">
              <h3>{modal.mode === 'create' ? 'Tạo tài khoản' : 'Chỉnh sửa tài khoản'}</h3>
              <button type="button" className="tamcare-followup-close" onClick={() => setModal(null)}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Username</span>
                <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Họ tên</span>
                <input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Email</span>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Số điện thoại</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Vai trò</span>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {roleOptions.slice(1).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span>Trạng thái</span>
                <select value={form.is_active ? '1' : '0'} onChange={(e) => setForm({ ...form, is_active: e.target.value === '1' })}>
                  <option value="1">Hoạt động</option>
                  <option value="0">Không hoạt động</option>
                </select>
              </label>
              {modal.mode === 'create' && (
                <label style={{ display: 'grid', gap: 6 }}>
                  <span>Mật khẩu</span>
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </label>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button className="tamcare-btn-ghost" onClick={() => setModal(null)}>Hủy</button>
              {modal.mode === 'create' ? (
                <button className="tamcare-btn-primary" onClick={submitCreate}>Tạo</button>
              ) : (
                <button className="tamcare-btn-primary" onClick={submitEdit}>Lưu</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
